// Manual de Usuario LIDER IA 2.0 — estructurado para el Centro de Ayuda.
// Fuente: Manual_Usuario_LIDER_IA_2.0.docx (ampliado con guías de uso paso a paso).

export type HelpSection = {
  heading: string;
  body: string; // párrafos separados por \n\n; líneas que inician con "- " se renderizan como lista.
};

export type HelpModule = {
  id: string;
  title: string;
  shortDescription: string;
  icon: string; // nombre del ícono lucide-react
  routePatterns: string[]; // rutas que activan este módulo
  sections: HelpSection[];
};

export type HelpGroup = {
  id: 'aprender' | 'gobernar';
  label: string;
  intro: string;
  modules: HelpModule[];
};

export const HELP_MANUAL: HelpGroup[] = [
  {
    id: 'aprender',
    label: 'Aprender e Innovar',
    intro:
      'Módulos para el usuario individual que quiere actualizarse, identificar oportunidades y crear proyectos reales con IA.',
    modules: [
      {
        id: 'tendencias',
        title: 'Tendencias',
        shortDescription: 'Mantente al día con las novedades de IA del mundo y de Latinoamérica.',
        icon: 'TrendingUp',
        routePatterns: ['/news', '/news/:articleId'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Una sección con las tendencias más relevantes de inteligencia artificial seleccionadas por el equipo de LIDER IA. Te permite estar al día sin tener que recorrer múltiples fuentes: noticias globales, casos en Latinoamérica, lanzamientos de herramientas y artículos analíticos.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Tendencias de IA" desde el menú izquierdo.\n2. En la portada verás los artículos destacados de la semana; explóralos primero para tener un panorama general.\n3. Usa el filtro de categorías (por ejemplo: Negocios, Tecnología, Casos, Regulación) para enfocar la lectura en tu área de interés.\n4. Haz clic en un artículo para ver el detalle completo, la fuente original y artículos relacionados.\n5. Comparte el link del artículo con tu equipo desde el botón de compartir.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Dedica 10 minutos al día, no una hora a la semana: la IA cambia muy rápido y consumir poco a poco es más sostenible.\n- Si lees un caso aplicable a tu industria, llévalo al Laboratorio de Reto y Solución como inspiración para un proyecto.\n- Marca como tema de conversación con Lía cualquier artículo que no entiendas: ella puede explicártelo en tu contexto.',
          },
        ],
      },
      {
        id: 'contenidos',
        title: 'Contenidos',
        shortDescription: 'Biblioteca de videos, simuladores y clases con Lía.',
        icon: 'BookOpen',
        routePatterns: ['/content', '/contenido'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Una biblioteca curada de contenido práctico de IA: videos paso a paso, simuladores interactivos y clases con Lía, la consultora de IA de la plataforma. Está organizada por categorías y niveles de madurez (0–5) para que veas lo que realmente te corresponde según tu diagnóstico.',
          },
          {
            heading: 'Formatos disponibles',
            body:
              '- Videos: el formato principal, con explicaciones aplicadas paso a paso.\n- Simuladores: contenidos interactivos para practicar.\n- Clase con Lía: una clase guiada por la IA consultora de la plataforma, que te hace preguntas para que aprendas de manera práctica.\n- Lecturas y PDFs: material complementario para profundizar.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Contenidos" desde el menú izquierdo.\n2. Filtra por categoría (por ejemplo: Productividad, Datos, Agentes), por nivel (0–5) o por tipo de contenido.\n3. Abre un contenido y míralo o complétalo en orden; los videos avanzan automáticamente al siguiente recomendado.\n4. Marca como completado cuando termines: esto alimenta tu progreso, tus métricas y tu racha.\n5. Si te queda alguna duda, abre el chat de Lía: ella conoce qué contenido acabas de ver.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Empieza por contenidos de tu nivel actual de madurez; subir demasiado rápido genera frustración.\n- Usa las "Clases con Lía" cuando quieras aprender practicando, no sólo mirando.\n- Si un contenido te inspira un proyecto, créalo de una vez desde "Proyectos" para no perder el impulso.',
          },
        ],
      },
      {
        id: 'diagnostico',
        title: 'Diagnóstico individual de madurez en IA',
        shortDescription: 'Conoce tu nivel actual de madurez en IA en 15 preguntas.',
        icon: 'Stethoscope',
        routePatterns: ['/diagnostic'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Una evaluación corta (15 preguntas) que determina tu nivel de madurez personal en inteligencia artificial. El resultado guía las recomendaciones de contenido, herramientas y proyectos que recibirás dentro de la plataforma.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a la sección "Diagnóstico" en el menú izquierdo.\n2. Ingresa tus datos básicos (cargo, área, industria).\n3. Responde las 15 preguntas con honestidad. Si no comprendes alguna, indícalo expresamente: esa también es información válida.\n4. Al finalizar verás tu nivel de madurez (de 0 a 5) con una explicación de qué significa.\n5. Revisa las recomendaciones automáticas de contenido y herramientas que la plataforma genera con base en tu resultado.',
          },
          {
            heading: 'Cómo interpretar tu nivel',
            body:
              '- Nivel 0–1: estás empezando; foco en conceptos básicos y prompts.\n- Nivel 2–3: ya usas IA en tu día a día; foco en flujos de trabajo y automatización.\n- Nivel 4–5: usuario avanzado; foco en agentes, integraciones y liderazgo de proyectos.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Repite el diagnóstico cada 2–3 meses para medir tu evolución.\n- Comparte el resultado con tu líder: ayuda a definir objetivos de formación.\n- Si tu organización tiene Plan Empresarial, tu nivel se agrega al diagnóstico empresarial.',
          },
        ],
      },
      {
        id: 'laboratorio',
        title: 'Laboratorio de Reto y Solución',
        shortDescription: 'Identifica un problema real y conviértelo en un proyecto de IA con un consultor virtual.',
        icon: 'Beaker',
        routePatterns: ['/challenge-lab'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Un consultor de IA conversacional que te ayuda a identificar tu primer reto y a transformarlo en un proyecto de IA bien formulado, listo para llevar al módulo de Proyectos.',
          },
          {
            heading: 'Metodologías que usa el consultor',
            body:
              'El consultor está entrenado con metodologías de mejora y análisis de procesos, entre ellas:\n\n- Lean Six Sigma\n- Value Stream Mapping (mapeo del flujo de valor)\n- Process Mining (minería de procesos)\n- DOFA (debilidades, oportunidades, fortalezas y amenazas)\n- 5W1H (qué, quién, cuándo, dónde, por qué y cómo)',
          },
          {
            heading: 'Los dos botones clave (arriba del chat)',
            body:
              'Mientras conversas con el consultor verás dos botones en la parte superior del chat:\n\n- Ver Reto: abre una ficha con el reto consolidado por la IA (título, descripción, público objetivo y KPI). Úsalo para revisar o recordar sobre qué reto estás trabajando.\n- Soluciones: abre el listado de las 5 soluciones de IA que el consultor propone al finalizar el análisis (una por cada tipo: Automatización, Asistente, Datos, Contenido y Coding). Desde ahí ves el detalle de cada una y eliges cuál convertir en proyecto.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Laboratorio de Reto y Solución" desde el menú izquierdo e inicia una nueva sesión.\n2. Fase 1 — Diagnóstico: responde a las preguntas del consultor sobre tu área, procesos, dolores y oportunidades. Cuanto más concreto seas, mejores serán las soluciones.\n3. Fase 2 — Identificación y análisis del reto: el consultor propone entre 1 y 5 retos candidatos; eliges uno y lo analiza con DOFA, 5W1H, Value Stream Mapping y Process Mining. Al cerrar la fase guarda el reto, y podrás abrirlo en el botón Ver Reto arriba del chat.\n4. Fase 3 — Propuesta de 5 soluciones: el consultor genera y registra automáticamente 5 soluciones de IA, una por cada tipo de solución del programa (Automatización, Asistente, Datos, Contenido, Coding). Aparecen en el botón Soluciones.\n5. Abre el botón Soluciones, revisa el detalle de cada opción (descripción, beneficios, complejidad) y compáralas.\n6. En el detalle de la solución elegida, pulsa "Convertir en proyecto": se crea automáticamente en el módulo de Proyectos con la ficha pre-llenada y el tipo de solución correcto, listo para abrir el asistente de prototipado.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Trae problemas concretos y medibles, no temas abstractos ("reducir el tiempo de cierre mensual" funciona mejor que "mejorar finanzas").\n- No te saltes preguntas: cada respuesta mejora la calidad de las 5 soluciones propuestas.\n- Usa el botón Ver Reto para releer el enunciado antes de decidir qué solución elegir.\n- Si no ves las 5 soluciones, vuelve a abrir el botón Soluciones: se registran automáticamente al terminar la Fase 3.\n- Puedes convertir más de una solución en proyecto si quieres comparar enfoques distintos.\n- Mantén una sesión por reto: si cambia el problema, abre una nueva sesión en lugar de mezclar conversaciones.',
          },

        ],
      },
      {
        id: 'proyectos',
        title: 'Proyectos',
        shortDescription: 'Tu centro de innovación: crea, prototipa y evalúa proyectos de IA acompañado por la plataforma.',
        icon: 'Target',
        routePatterns: ['/challenge', '/challenge/showcase', '/project/:projectId'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'El módulo donde LIDER IA deja de ser sólo una plataforma de aprendizaje y se vuelve de innovación: identificas un proyecto y recibes acompañamiento de la IA para construirlo de principio a fin.',
          },
          {
            heading: 'La ficha del proyecto',
            body:
              'Al ingresar a un proyecto encuentras información que puedes modificar en cualquier momento:\n\n- Título, público objetivo y problema que resuelve.\n- Tipo de solución (de los 5 tipos: contenido, datos, agentes, automatizaciones o aplicaciones).\n- Recursos recomendados por la plataforma.\n- Asistente de prototipado especializado por tipo de solución.\n- Evaluación de impacto, relevancia, seguridad y escalabilidad.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Crea el proyecto desde el Laboratorio (recomendado) o manualmente desde "Proyectos > Nuevo".\n2. Completa la ficha: título, descripción, público, problema, tipo de solución.\n3. Revisa los recursos recomendados (videos, herramientas, lecturas) y consúmelos.\n4. Abre el "Asistente de prototipado": es una IA especializada según el tipo de solución que elegiste.\n5. Sube tus entregables (documento, demo, código, video) en cada hito.\n6. Mira la evaluación automática (impacto, riesgo, escalabilidad) y ajusta tu proyecto.\n7. Si estás en una ruta, marca el proyecto como entregable final.',
          },
          {
            heading: 'Los 5 tipos de solución',
            body:
              '- Contenido (textos, imágenes, materiales generados con IA).\n- Datos (análisis, dashboards, predicciones).\n- Agentes (asistentes conversacionales).\n- Automatizaciones (flujos sin intervención humana).\n- Aplicaciones (productos digitales con IA).\n\nPara certificarte en una ruta debes haber trabajado los 5 tipos a lo largo del programa.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Empieza pequeño: un prototipo funcional vale más que un plan perfecto.\n- Usa el asistente de prototipado como copiloto, no como hacedor: tú decides el diseño.\n- Antes de cerrar el proyecto revisa los criterios de evaluación: el sistema te dice qué te falta.',
          },
        ],
      },
      {
        id: 'herramientas',
        title: 'Herramientas de IA',
        shortDescription: 'Catálogo curado de las mejores herramientas de IA, abierto a la comunidad.',
        icon: 'Wrench',
        routePatterns: ['/ai-tools', '/herramientas-ia'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Un catálogo curado por el equipo de LIDER IA con las mejores herramientas de IA del mercado. Es dinámico y abierto: los usuarios también pueden recomendar nuevas herramientas para incluir.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Herramientas de IA" desde el menú izquierdo.\n2. Filtra por categoría (productividad, datos, marketing, desarrollo, diseño, etc.) o por nivel de madurez recomendado.\n3. Abre una herramienta para ver su ficha: para qué sirve, casos de uso, costo, integraciones y nivel sugerido.\n4. Si la herramienta encaja con tu trabajo, marca "Probar" para guardarla en tu stack personal.\n5. Si conoces una herramienta excelente que no está en el catálogo, usa "Recomendar herramienta" para proponerla al equipo curador.',
          },
          {
            heading: 'Stack recomendado',
            body:
              'La plataforma te recomienda automáticamente un stack adecuado a tu nivel actual (0–5) en el diagnóstico. No intentes adoptar todas las herramientas al tiempo: empieza por 2–3 que se conecten con tus tareas reales.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Antes de pagar una herramienta, valida que tu nivel actual te permita explotarla.\n- Verifica las herramientas aprobadas por tu empresa (si tienes Plan Empresarial) para evitar problemas de seguridad.\n- Comparte en la Comunidad cómo te fue con cada herramienta: ayuda a otros a decidir.',
          },
        ],
      },
      {
        id: 'comunidad',
        title: 'Comunidad',
        shortDescription: 'Pregunta, responde y encuentra compañeros para tus proyectos.',
        icon: 'MessageCircle',
        routePatterns: ['/forum', '/forum/:threadId'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Un espacio donde todos los miembros se ayudan compartiendo conocimiento. Se accede desde el menú izquierdo.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Comunidad" desde el menú izquierdo.\n2. Antes de publicar, usa el buscador: muy probablemente alguien ya hizo tu misma pregunta.\n3. Si no encuentras la respuesta, abre un nuevo hilo: título claro, descripción del contexto y qué has intentado.\n4. Etiqueta el tema (por ejemplo: prompts, herramientas, proyectos) para que llegue a las personas correctas.\n5. Vuelve a responder en tu hilo cuando logres resolverlo: cierras el ciclo y ayudas a otros.',
          },
          {
            heading: 'Qué puedes hacer',
            body:
              '- Hacer una pregunta sobre cualquier tema de IA.\n- Buscar primero si alguien ya hizo esa pregunta (frecuentemente la respuesta ya existe).\n- Responder preguntas de otros y aportar tu experiencia.\n- Buscar compañeros para desarrollar un proyecto en conjunto.\n- Marcar una respuesta como "solución" cuando te resuelva el caso.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Aporta antes de pedir: responder 1–2 preguntas al mes te posiciona como referente.\n- Sé específico: "no me funciona" no permite ayudarte; comparte el prompt, la herramienta y el resultado.\n- Respeta la confidencialidad: no compartas datos sensibles de tu empresa en hilos públicos.',
          },
        ],
      },
      {
        id: 'lia',
        title: 'Chat con Lía, tu consultora de IA',
        shortDescription: 'Tu acompañante 24/7 dentro de la plataforma.',
        icon: 'Sparkles',
        routePatterns: [],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Lía es la consultora de IA de la plataforma. Está disponible siempre desde el botón "Chat con LIA" del menú izquierdo. Conoce tu contexto (diagnóstico, proyectos, contenido visto, ruta en curso) y responde con esa información.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Abre "Chat con LIA" desde el menú izquierdo en cualquier momento.\n2. Escribe tu pregunta en lenguaje natural; no necesitas prompts complicados.\n3. Si Lía no tiene contexto suficiente, te lo va a preguntar; respóndele y continúa.\n4. Usa el historial de sesiones para retomar conversaciones anteriores; cada sesión tiene su propio título.\n5. Cuando termines, puedes marcar la respuesta como útil; eso ayuda a mejorar el sistema.',
          },
          {
            heading: 'Cuándo usarla',
            body:
              '- Cuando tengas dudas sobre cómo usar un módulo o función de la plataforma.\n- Cuando necesites ideas o resúmenes para tu proyecto actual.\n- Cuando quieras una explicación adaptada a tu nivel sobre un concepto de IA.\n- Cuando estés trabajando en una clase con Lía: ella te guía paso a paso.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Dale contexto: "soy gerente de operaciones y quiero..." mejora muchísimo la calidad de la respuesta.\n- Pídele que reformule si la respuesta es muy técnica o muy general.\n- Combina Lía con el asistente de prototipado: Lía para conceptos, prototipado para construir.',
          },
        ],
      },
      {
        id: 'perfil-soporte',
        title: 'Perfil de usuario y soporte técnico',
        shortDescription: 'Tu cuenta, racha, certificaciones, plan y canal de soporte.',
        icon: 'UserCircle',
        routePatterns: ['/profile', '/support'],
        sections: [
          {
            heading: 'Tu perfil',
            body:
              'En tu perfil encuentras:\n\n- Información personal: tus datos básicos.\n- Certificaciones: las certificaciones de LIDER IA que hayas obtenido.\n- Racha: cuántos días seguidos has usado la plataforma. Si estás en una ruta con acompañamiento (cohorte), verás el ranking de tu cohorte y podrás cambiar de cohorte con el selector. Si estás en una ruta de autoaprendizaje o no estás en ninguna ruta, tu racha es personal: no hay ranking ni comparación con nadie, tu reto es superar tu mejor racha.\n- Plan: acceso a la información de tu Plan activo.\n- Recomendaciones y sugerencias: un espacio para dejarnos comentarios al equipo.\n- Referidos (próximamente): para ganar beneficios al referir la plataforma.',
          },
          {
            heading: 'Paso a paso (perfil)',
            body:
              '1. Entra a "Mi Perfil" desde el avatar arriba a la derecha o desde el menú.\n2. Actualiza tus datos básicos (cargo, área, foto) para que las recomendaciones sean más precisas.\n3. Revisa tu racha; entrar al menos 5 minutos al día la mantiene activa. En cohortes verás además el ranking de tus compañeros de cohorte; en autoaprendizaje la medición es personal.\n4. Descarga tus certificaciones obtenidas desde la sección "Certificaciones".\n5. Si tienes una idea de mejora, déjala en "Recomendaciones y sugerencias".',

          },
          {
            heading: 'Soporte técnico',
            body:
              'Si necesitas ayuda específica, abre un ticket desde "Soporte":\n\n1. Entra a "Soporte" en el menú.\n2. Crea un nuevo ticket con título claro y describe el problema.\n3. Adjunta capturas de pantalla o videos cortos: aceleran muchísimo la respuesta.\n4. Sigue el estado del ticket (pendiente, en proceso, resuelto) y responde si el equipo te pide más información.\n5. Cuando se resuelva, márcalo como "solucionado".',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Antes de abrir un ticket, prueba preguntarle a Lía o buscar en la Comunidad.\n- Reporta un problema por ticket: facilita el seguimiento.\n- Si pierdes tu racha, no pasa nada: vuelve a empezar al día siguiente.',
          },
        ],
      },
      {
        id: 'rutas',
        title: 'Rutas LIDER IA',
        shortDescription: 'Dos formas de aprender: rutas de autoaprendizaje a tu ritmo o rutas con acompañamiento en vivo y certificación.',
        icon: 'GraduationCap',
        routePatterns: ['/calendar', '/rutas', '/rutas/:certificationId'],
        sections: [
          {
            heading: '¿Qué tipos de rutas hay?',
            body:
              'En "Rutas" del menú izquierdo encuentras dos modalidades, con dos pestañas:\n\n- Rutas de Autoaprendizaje: empiezas cuando quieras y avanzas a tu ritmo, con un plan de fechas sugeridas semana a semana. No hay sesiones en vivo ni entregables: tú marcas el paso.\n- Rutas con Acompañamiento: perteneces a una cohorte con calendario, sesiones en vivo con mentores expertos, entregables semanales y proyecto final. Al completar todo obtienes la certificación.',
          },
          {
            heading: '¿Qué incluye una ruta de autoaprendizaje?',
            body:
              '- Capítulos con contenidos (videos, lecturas, interactivos y más) que consumes a tu ritmo.\n- Un plan semanal con fechas sugeridas para cada capítulo, calculado desde tu fecha de inicio.\n- Progreso de contenidos obligatorios: al completarlos todos, la ruta queda completada.\n- Botón "Replanificar" para recalcular tus fechas sugeridas desde hoy, sin perder tu progreso.\n- Diploma digital descargable al completar la ruta.',
          },
          {
            heading: '¿Qué incluye una ruta con acompañamiento?',
            body:
              '- Calendario: vista en detalle del contenido a ver cada semana, las sesiones en vivo y lo que sigue.\n- Sesiones en vivo con mentores expertos.\n- Entregables semanales obligatorios.\n- Proyecto final: para certificarte debes entregar un proyecto (con fecha límite) y completar los entregables obligatorios de cada semana.\n- Diploma digital descargable al certificarte.',
          },
          {
            heading: 'Paso a paso (autoaprendizaje)',
            body:
              '1. Entra a "Rutas" desde el menú izquierdo y quédate en la pestaña "Rutas de Autoaprendizaje".\n2. Elige la ruta que quieras y revísala en "Ver info" para conocer su nivel y contenido.\n3. Inscríbete: la ruta queda en tu lista y tu plan se calcula desde esa fecha.\n4. Sigue el plan semanal: abre cada capítulo y marca los contenidos completados al terminarlos.\n5. Si te atrasas, usa "Replanificar" para recalcular las fechas sugeridas desde hoy, sin perder tu progreso.\n6. Al completar todos los contenidos obligatorios, la ruta se marca como completada y puedes descargar tu diploma.',
          },
          {
            heading: 'Paso a paso (con acompañamiento)',
            body:
              '1. Entra al "Calendario" desde el menú izquierdo (sólo visible si estás inscrito en una cohorte activa).\n2. Mira la semana actual: qué contenido debes ver, qué sesión hay y qué entregable vence.\n3. Consume el contenido de la semana antes de la sesión en vivo.\n4. Asiste a la sesión en vivo; queda grabada por si no puedes ir.\n5. Sube el entregable de la semana antes de la fecha límite.\n6. En paralelo, avanza en tu proyecto final usando los 5 tipos de solución.\n7. Antes del cierre, entrega el proyecto final completo para acceder a tu diploma.',
          },
          {
            heading: '¿Cómo se accede?',
            body:
              'Las rutas de autoaprendizaje están incluidas en tu plan: te inscribes directamente desde la pestaña "Rutas de Autoaprendizaje".\nLas rutas con acompañamiento se acceden a través de tu empresa, si contrata el acompañamiento (Plan Empresarial Ruta), o adquiriéndolas directamente cuando estén abiertas al público desde "Rutas".',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- En autoaprendizaje, marca cada contenido al terminarlo para no perder el progreso; tu avance se guarda solo con eso.\n- Si te atrasas en autoaprendizaje, no pasa nada: usa "Replanificar" y las fechas sugeridas se recalculan desde hoy, sin penalización.\n- En rutas con acompañamiento, no dejes los entregables para el último día: la calidad cae y bloqueas tu certificación.\n- Aprovecha las sesiones en vivo para preguntar sobre tu proyecto, no sólo sobre la teoría.\n- Si vas atrasado en una cohorte, habla con tu mentor; suele haber margen, pero hay que avisar a tiempo.',
          },
        ],
      },
    ],
  },
  {
    id: 'gobernar',
    label: 'Gestionar y Gobernar',
    intro:
      'Módulos para el administrador de empresa: diagnosticar, gestionar usuarios y rutas, gobernar la IA y medir impacto.',
    modules: [
      {
        id: 'rol-admin',
        title: 'El rol administrador y el menú de empresa',
        shortDescription: 'Cómo está organizado el menú cuando entras como administrador.',
        icon: 'Shield',
        routePatterns: [],
        sections: [
          {
            heading: '¿Qué hace el administrador?',
            body:
              'El administrador es quien gestiona la adopción de IA en la empresa: diagnóstico, usuarios, rutas, portafolio de proyectos, riesgos y gobierno. Un mismo usuario puede tener perfil de innovador y de administrador, y alternar entre ambos sin perder su contexto.',
          },
          {
            heading: 'El menú de empresa',
            body:
              'Cuando cambias al rol "Administrador" desde tu menú de usuario, el menú izquierdo se reorganiza con los módulos de gestión y gobernanza:\n\n- Diagnóstico empresarial.\n- Dashboard empresarial.\n- Gestión de usuarios y Planes.\n- Gestión de rutas y cohortes.\n- Portafolio de proyectos de IA.\n- Inventario de IA (Shadow AI).\n- Gestión de riesgos.\n- Centro de Excelencia.\n- Gestión del cambio y rediseño de roles.',
          },
          {
            heading: 'Paso a paso para empezar',
            body:
              '1. Cambia al rol "Administrador" desde el selector arriba a la derecha.\n2. Si es tu primera vez, completa el Diagnóstico empresarial: define la línea base.\n3. Carga usuarios (manual o por importación) en "Gestión de usuarios".\n4. Crea o asocia una cohorte si tu organización tiene una ruta contratada.\n5. Revisa el Dashboard empresarial para ver el avance inicial.\n6. Configura el Centro de Excelencia y publica los lineamientos de uso de IA.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- No intentes activarlo todo el primer día: prioriza diagnóstico, usuarios y dashboard.\n- Mantén actualizada la lista de usuarios: las métricas dependen de ella.\n- Revisa el dashboard una vez por semana para detectar cambios temprano.',
          },
        ],
      },
      {
        id: 'diagnostico-empresarial',
        title: 'Diagnóstico empresarial',
        shortDescription: 'Evalúa el nivel de madurez de IA de toda la organización en 4 dimensiones.',
        icon: 'ClipboardList',
        routePatterns: ['/enterprise-diagnostic'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'Una evaluación cualitativa que califica a la empresa en su nivel de madurez para adoptar IA, con scoring asistido por IA sobre 15 subdimensiones agrupadas en 5 dimensiones que forman el modelo L‑I‑D‑E‑R.',
          },
          {
            heading: 'Dimensiones (modelo L‑I‑D‑E‑R)',
            body:
              '- L — Liderazgo y Estrategia.\n- I — Innovación.\n- D — Datos y Tecnología (arquitectura, infraestructura e integración).\n- E — Ética y Gobernanza (cumplimiento y gestión de riesgos).\n- R — Roles y Talento.',

          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Diagnóstico empresarial" desde el menú de administrador.\n2. Responde cada subdimensión: la plataforma te pide texto cualitativo, no sólo opciones.\n3. Sé descriptivo: la IA evalúa el contenido de tu respuesta, mientras más detalles, más preciso el score.\n4. Guarda como borrador si necesitas pausar; puedes retomar más tarde.\n5. Cuando termines, lanza la evaluación; recibirás puntajes por subdimensión, dimensión y total.\n6. Los resultados quedan disponibles en el Dashboard empresarial y guían las acciones recomendadas.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Hazlo con 2–3 personas clave (TI, Talento Humano, Operaciones): da una visión más realista.\n- Repítelo cada 6 meses para medir avance.\n- Comparte los resultados con el comité directivo: es la base para priorizar inversiones.',
          },
        ],
      },
      {
        id: 'usuarios-planes',
        title: 'Gestión de usuarios y planes',
        shortDescription: 'Administra los miembros y los planes activos en tu organización.',
        icon: 'Users',
        routePatterns: ['/user-management'],
        sections: [
          {
            heading: '¿Qué puedes hacer?',
            body:
              '- Invitar y dar de baja usuarios.\n- Asignar roles (participante, mentor, administrador).\n- Ver el consumo del Plan contratado y los usuarios activos por organización.\n- Reasignar usuarios entre cohortes.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Gestión de usuarios" desde el menú de administrador.\n2. Para agregar uno: clic en "Invitar usuario", ingresa correo, rol y (opcional) cohorte.\n3. Para invitar varios: usa la importación por archivo CSV y revisa la previsualización antes de confirmar.\n4. Para cambiar el rol: abre la ficha del usuario y selecciona el nuevo rol.\n5. Para dar de baja: usa la acción "Desactivar"; libera el cupo de tu Plan.\n6. Revisa el contador de usuarios activos vs. cupo contratado en la cabecera.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Mantén actualizado el listado para que las métricas y el dashboard empresarial reflejen la realidad de adopción.\n- Desactiva a los usuarios que salgan de la empresa: liberas cupos y proteges datos.\n- Si tu cupo está lleno, contacta al equipo para ampliar el Plan antes de invitar más usuarios.',
          },
        ],
      },
      {
        id: 'cohortes',
        title: 'Gestión de rutas y cohortes',
        shortDescription: 'Consulta las cohortes de tu organización y gestiona los inscritos.',
        icon: 'Calendar',
        routePatterns: ['/cohort-management'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'El espacio donde ves las cohortes que tu organización tiene contratadas, gestionas los participantes inscritos y revisas el calendario, avance y certificados de cada cohorte. La creación y configuración estructural de las cohortes y rutas la realiza el equipo LIDER IA.',
          },
          {
            heading: 'Qué puedes hacer como Admin de Empresa',
            body:
              '- Ver el listado de cohortes de tu organización (filtrado automáticamente).\n- Inscribir o dar de baja participantes en cada cohorte.\n- Consultar el progreso de los participantes, entregables y rachas.\n- Revisar el calendario académico y las fechas clave (sesiones en vivo, entregables, deadline de proyecto y de Laboratorio de Reto).\n- Consultar la configuración del Demo Day.\n- Ver los certificados generados.\n- Pulsar "Solicitar contratación de cohorte" para pedir al equipo LIDER IA una cohorte nueva.',
          },
          {
            heading: 'Acciones reservadas al equipo LIDER IA',
            body:
              'Las siguientes acciones aparecen como solo lectura para que puedas revisarlas, pero no modificarlas:\n\n- Crear, duplicar o eliminar cohortes.\n- Editar datos generales (nombre, ruta, fechas, organización).\n- Editar el calendario académico (sesiones, mentorías, fechas).\n- Configurar el Demo Day.\n- Crear rutas nuevas.\n\nSi necesitas cualquiera de estos cambios, usa el botón "Solicitar contratación de cohorte" o contacta al equipo LIDER IA.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Gestión de rutas y cohortes" desde el menú de administrador.\n2. Si tu organización aún no tiene cohorte activa, pulsa "Solicitar contratación de cohorte" y completa la solicitud.\n3. Cuando la cohorte aparezca en el listado, ábrela con el icono de ojo para ver su configuración.\n4. Ve a la pestaña Participantes y usa "Inscribir" para agregar correos uno por uno o por importación.\n5. Revisa la pestaña Calendario para conocer fechas de sesiones en vivo y entregables (vista de solo lectura).\n6. Usa la pestaña Progreso durante el programa para monitorear avance, entregables y rachas.\n7. Consulta la pestaña Demo Day para conocer la configuración del cierre.\n8. Al finalizar, entra a Certificados para descargar los diplomas generados.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Si necesitas cambiar fechas, nombre o calendario de una cohorte, solicítalo al equipo LIDER IA: la configuración estructural es solo lectura.\n- Inscribe a los participantes antes del inicio para que reciban el calendario sincronizado en su perfil.\n- Verifica que los correos invitados existan como usuarios en tu organización (módulo "Gestión de usuarios").\n- Usa la pestaña Progreso semanalmente: detecta a tiempo a los participantes rezagados.\n- No borres ítems del calendario; comunícalos al equipo LIDER IA para preservar las relaciones con entregables existentes.',
          },
        ],
      },
      {
        id: 'portafolio',
        title: 'Portafolio de proyectos de IA',
        shortDescription: 'Visión consolidada y calificación LIDER-IA de los proyectos de IA de la empresa.',
        icon: 'Layers',
        routePatterns: ['/ai-portfolio', '/ai-portfolio/:projectId'],
        sections: [
          {
            heading: '¿Qué es?',
            body:
              'La vista consolidada de todos los proyectos de IA que están desarrollando tus colaboradores, con calificación automática bajo el marco LIDER-IA y herramientas para priorizar, comparar y detectar sinergias.',
          },
          {
            heading: 'Marco de evaluación LIDER-IA (12 componentes, escala 1-5 → puntaje 0-500)',
            body:
              'Cada proyecto se califica en 12 componentes agrupados en las 5 dimensiones del modelo L-I-D-E-R. La calificación se pondera con pesos configurables y produce un puntaje total entre 0 y 500.\n\n- L — Liderazgo y Estrategia: Alineación Estratégica · Impacto esperado / ROI.\n- I — Innovación: Optimización de Procesos · Madurez del Prototipo.\n- D — Datos y Tecnología: Disponibilidad y calidad de datos · Factibilidad y Complejidad · Escalabilidad y reutilización · MLOps, Monitorización y Sostenibilidad.\n- E — Ética y Gobernanza: Gobernanza & Ética + Riesgos y Seguridad · Explicabilidad (XAI).\n- R — Roles y Talento: Responsabilidad Algorítmica y Supervisión Humana · Adopción y Gestión del Cambio.',
          },
          {
            heading: 'Matriz de Priorización y Acción',
            body:
              'Según el puntaje total cada proyecto se clasifica automáticamente:\n\n- 400 – 500 · Quick Win → escalar en ≤ 3 meses. Proyecto maduro, alto retorno, bajo riesgo.\n- 300 – 399 · Strategic Play → piloto ampliado (3-6 meses). Ajustes menores antes de escalar.\n- 200 – 299 · Experiment → mantener en Sandbox; refinar hipótesis y mejorar datos/procesos.\n- < 200 · Back-log → archivar o replantear. Riesgo muy alto o valor estratégico insuficiente.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Portafolio de proyectos de IA" desde el menú de administrador.\n2. Pestaña Portafolio: filtra por estado, tipo de solución, área o clasificación. Cambia entre vista tabla/cards. Selecciona varios proyectos para usar Evaluación masiva, Comparar o Exportar PDF. Abre un proyecto para ver su ficha completa y la rúbrica LIDER-IA.\n3. Pestaña Framework: revisa el scatter de proyectos, el pie de clasificación y la Matriz 0-500. Si lo necesitas, ajusta los pesos personalizados de los 12 componentes para alinear la evaluación a la estrategia de tu empresa.\n4. Pestaña Análisis de ROI: revisa los indicadores económicos agregados (inversión, beneficios, ROI %, BCR y payback) de los proyectos que ya tienen datos de ROI cargados.\n5. Pestaña Sinergias: pulsa "Analizar sinergias" para que la IA detecte proyectos solapados y proponga consolidaciones.\n6. Usa "Exportar a Excel" arriba a la derecha para llevar el portafolio a comités o reportes externos.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Calibra los pesos a la estrategia de tu empresa (no uses los valores por defecto sin revisarlos).\n- Lanza una Evaluación masiva cada vez que termine una cohorte para nivelar el portafolio.\n- Usa Sinergias antes de aprobar un proyecto nuevo: evita duplicar esfuerzo.\n- Prioriza los Quick Win para mostrar resultados rápidos al comité.\n- Mueve los Back-log a archivado para mantener limpio el portafolio activo.',
          },
        ],
      },
      {
        id: 'dashboard-empresarial',
        title: 'Dashboard empresarial',
        shortDescription: 'KPIs consolidados de diagnósticos, cohortes, proyectos, adopción, herramientas, NPS y ROI.',
        icon: 'Building2',
        routePatterns: ['/enterprise-dashboard'],
        sections: [
          {
            heading: '¿Qué muestra?',
            body:
              'Siete pestañas con los indicadores clave consolidados de tu organización: madurez en IA, avance de cohortes, estado del portafolio, adopción de la plataforma, uso de herramientas, satisfacción (NPS) e impacto económico (ROI).',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Dashboard empresarial" desde el menú de administrador.\n2. Diagnósticos: revisa la distribución de tus colaboradores por nivel de madurez en IA y por cada dimensión L-I-D-E-R.\n3. Cohorte: selecciona una cohorte y revisa su avance total y el de la semana actual (contenidos consumidos, sesiones, entregables y rachas).\n4. Proyectos: revisa el estado del portafolio (cantidad, distribución por tipo de solución y clasificación LIDER-IA).\n5. Adopción: usuarios activos, frecuencia de uso y constancia (rachas) en la plataforma.\n6. Herramientas: cuáles del catálogo se están usando más en la organización.\n7. NPS: encuestas de satisfacción del programa de formación.\n8. ROI: impacto económico agregado de los proyectos del portafolio.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Define 3 KPIs clave para tu organización (por ejemplo: usuarios activos semanales, % de proyectos en Strategic Play o más, ROI agregado) y revísalos cada semana.\n- Si Adopción baja, investiga qué cambió (comunicación, sesiones, herramientas).\n- Cruza Diagnósticos y Proyectos: brecha entre nivel de madurez declarado y tipo de proyectos avanzados.\n- Comparte el dashboard en comités: alinea conversaciones con datos reales.',
          },
        ],
      },
      {
        id: 'inventario-ia',
        title: 'Inventario de IA (control del Shadow AI)',
        shortDescription: 'Inventario centralizado de herramientas, modelos, datos, soluciones y apps con IA.',
        icon: 'FileSearch',
        routePatterns: ['/ai-inventory'],
        sections: [
          {
            heading: 'El problema: Shadow AI',
            body:
              'El Shadow AI es un problema serio: la organización no sabe qué herramientas de IA están usando sus colaboradores. Sin visibilidad no hay control de licencias, ni de seguridad, ni de datos.',
          },
          {
            heading: '¿Qué hace el módulo?',
            body:
              'Centraliza el inventario de IA de tu organización en cinco categorías y te muestra KPIs de uso, costo y cobertura para que detectes herramientas no oficiales, licencias subutilizadas y modelos parados en desarrollo.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Inventario de IA" desde el menú de administrador. La cabecera te muestra 6 KPIs: Herramientas activas, Costo mensual, Uso de licencias (%), Modelos en producción, Fuentes de datos y Apps con IA.\n2. Herramientas IA: registra cada herramienta contratada (vendor, categoría, licencias contratadas vs usadas, costo). El sistema calcula automáticamente el costo mensual total y el % de uso.\n3. Modelos IA: documenta los modelos en uso (estado producción/desarrollo, propósito, datos que procesa, responsable).\n4. Fuentes de Datos: cataloga las bases y conectores que alimentan tus soluciones de IA.\n5. Soluciones: registra las soluciones propias desplegadas; verás los proyectos asociados a cada una.\n6. Aplicaciones: lista del software empresarial y marca cuáles ya integran IA.\n7. Usa los KPIs de la cabecera para identificar licencias subutilizadas y modelos que llevan tiempo sin pasar a producción.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Haz una encuesta interna al iniciar: la mayoría del Shadow AI sale a la luz cuando preguntas.\n- Revisa licencias subutilizadas cada mes: es donde más se pierde dinero.\n- Mantén un canal para que los colaboradores soliciten herramientas nuevas oficialmente.\n- Antes de promover un modelo a producción, asegúrate de que tenga responsable, propósito y datos documentados.\n- Revisa el inventario trimestralmente: la oferta de IA cambia rápido.',
          },
        ],
      },
      {
        id: 'riesgos',
        title: 'Gestión de riesgos de IA',
        shortDescription: 'Mapa de riesgos por proyecto, matriz 5×5 y controles recomendados (NIST AI, ISO 42001).',
        icon: 'AlertTriangle',
        routePatterns: ['/risk-management'],
        sections: [
          {
            heading: '¿Qué hace?',
            body:
              '- Identifica riesgos a partir de tus proyectos de IA.\n- Los ubica en una matriz 5×5 de probabilidad × impacto.\n- Recomienda controles basados en NIST AI e ISO 42001 y monitorea cuáles están implementados.\n- Te ayuda a llevar todos los proyectos de la zona roja a la zona verde antes de pasar a producción.',
          },
          {
            heading: 'Tipos de riesgo que cubre',
            body:
              'Seguridad, sesgo algorítmico, propiedad intelectual, privacidad y protección de datos, cumplimiento legal y regulatorio, entre otros.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Centro de Gestión de Riesgos" desde el menú de administrador.\n2. Riesgos Identificados: revisa la lista de riesgos detectados a partir de tus proyectos. Abre uno para ver descripción, categoría y proyectos afectados.\n3. Matriz de Riesgos 5×5: visualización probabilidad × impacto; los riesgos en la zona roja requieren acción inmediata.\n4. Controles: revisa los controles recomendados (basados en NIST AI / ISO 42001), marca los implementados, asigna responsable y fecha, y carga evidencia (documento, captura o enlace).\n5. Dentro del detalle de un riesgo dispones de sub-pestañas: Evaluación (calibra probabilidad/impacto), Controles (gestión específica del riesgo) y Proyectos (qué proyectos lo originan).\n6. Meta: que ningún proyecto pase a producción con riesgos en zona roja sin controles activos.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Gestiona los riesgos durante el desarrollo, no al final del proyecto.\n- Cruza este módulo con el Portafolio: un proyecto en rojo no debería avanzar a aprobación.\n- Forma a 1–2 personas en NIST AI / ISO 42001 para interpretar correctamente los controles.\n- Carga evidencia siempre que marques un control: te servirá para auditorías.',
          },
        ],
      },
      {
        id: 'coe',
        title: 'Centro de Excelencia IA',
        shortDescription: 'Configuración estratégica, operativa y normativa del CoE de tu organización.',
        icon: 'Sparkles',
        routePatterns: ['/coe-management'],
        sections: [
          {
            heading: '¿Qué configuras?',
            body:
              'El Centro de Excelencia (CoE) es donde defines los lineamientos oficiales de IA de la organización: visión y objetivos, modelo operativo, roles clave, políticas, marco regulatorio aplicable y procesos internos.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Centro de Excelencia IA" desde el menú de administrador.\n2. Configuración: define la configuración estratégica del CoE (visión, misión, objetivos y alcance).\n3. Modelo Operativo: elige el modelo (centralizado, federado o hub-and-spoke) y cómo se relaciona con las áreas del negocio.\n4. Roles Clave: documenta los roles del CoE (líder, arquitecto IA, data scientist, change manager, ethics officer, etc.) y a quién los asignas.\n5. Políticas: publica las políticas oficiales de uso de IA (datos sensibles, propiedad intelectual, herramientas permitidas, uso de prompts).\n6. Marco Regulatorio: registra las normativas aplicables a tu organización (AI Act, ISO 42001, NIST AI, GDPR, HABEAS DATA, etc.).\n7. Procesos: define los procesos del CoE (intake de iniciativas, evaluación, aprobación, monitoreo y desactivación).',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Arranca con políticas mínimas pero claras; ya las irás ampliando.\n- Comunica el CoE en un evento interno: le da legitimidad.\n- Revisa los lineamientos cada 6 meses: la IA y la regulación cambian rápido.\n- Alinea Roles Clave con tu organigrama real; los roles "ficticios" no funcionan.\n- Antes de redactar políticas, revisa el Marco Regulatorio: te ahorra reescribirlas después.',
          },
        ],
      },
      {
        id: 'cambio-roles',
        title: 'Gestión del cambio y rediseño de roles',
        shortDescription: 'Plan de gestión del cambio (Kotter + ADKAR) y rediseño de roles con apoyo de IA.',
        icon: 'RefreshCw',
        routePatterns: ['/change-management'],
        sections: [
          {
            heading: '¿Qué hay dentro?',
            body:
              'El módulo se organiza en dos tarjetas:\n\n- Plan de Gestión del Cambio: checklist guiado que combina las 8 etapas de Kotter con las 5 fases de ADKAR (Awareness, Desire, Knowledge, Ability, Reinforcement). Muestra el % de avance.\n- Rediseño de Roles: análisis de cargos asistido por IA para identificar qué tareas se pueden automatizar, aumentar con IA o conservar como humanas puras. Muestra cuántos roles llevas rediseñados.',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Gestión del cambio y rediseño de roles" desde el menú de administrador.\n2. Entra a Plan de Gestión del Cambio y recorre el checklist de las 8 etapas de Kotter cruzadas con las 5 fases de ADKAR. Marca tareas completadas; el % avanza automáticamente.\n3. Entra a Rediseño de Roles y crea un nuevo análisis; sube el manual de funciones del cargo.\n4. La IA clasifica las tareas en tres categorías: Automatizables (las hace la IA sola), Aumentables (humano + IA) y Humanas puras (no conviene automatizar).\n5. Ajusta las categorías donde no estés de acuerdo (la IA propone, tú decides) y obtén la propuesta de rediseño del cargo.\n6. Genera el informe final y compártelo con el líder del área y Talento Humano.\n7. Vuelve cada semana al checklist Kotter+ADKAR para no perder ritmo en la adopción.',
          },
          {
            heading: 'Tips y buenas prácticas',
            body:
              '- Empieza por cargos con alto volumen y tareas repetitivas: el ROI se nota más rápido.\n- Involucra desde el inicio al titular del cargo: el rediseño debe sentirse como oportunidad, no amenaza.\n- Combina rediseño con formación: un cargo rediseñado sin capacitación fracasa.\n- No avances a una etapa de Kotter sin cerrar la anterior; el orden importa.\n- Usa ADKAR para diagnosticar dónde se atasca la adopción (¿falta Awareness? ¿falta Ability?).',
          },
        ],
      },
      {
        id: 'planes',
        title: 'Planes',
        shortDescription: 'Información de planes disponibles y el plan activo de tu organización.',
        icon: 'CreditCard',
        routePatterns: ['/planes'],
        sections: [
          {
            heading: '¿Qué incluye?',
            body:
              'La comparación de Planes disponibles, sus beneficios, y la gestión del Plan activo (renovación, facturación, ampliación de usuarios).',
          },
          {
            heading: 'Paso a paso',
            body:
              '1. Entra a "Planes" desde el menú.\n2. Compara los Planes disponibles y sus beneficios (usuarios, rutas incluidas, módulos avanzados).\n3. Si quieres cambiar de Plan, contacta al equipo desde el botón "Hablar con ventas".\n4. Para ver detalles del Plan activo, factura o método de pago, entra a "Mi Perfil > Pagos".\n5. Desde allí puedes descargar facturas y administrar tu suscripción.',
          },
          {
            heading: 'Tip',
            body:
              'Si vas a ampliar usuarios cerca del fin de mes, solicítalo con anticipación para que el cupo esté disponible cuando lo necesites.',
          },
          {
            heading: 'Buenas prácticas',
            body:
              '- Revisa el consumo del Plan una vez al mes en "Gestión de usuarios".\n- Mantén guardadas las facturas para tu área contable.\n- Si tu Plan vence, anticipa la renovación: evita que los usuarios pierdan acceso.',
          },
        ],
      },
    ],
  },
];

export function getAllModules(): HelpModule[] {
  return HELP_MANUAL.flatMap((g) => g.modules);
}
